import {useState, useEffect, forwardRef, useContext} from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import CloseIcon from "@mui/icons-material/Close";
import NumberOfRounds from "./NumberOfRounds";
import GroupSize from "./GroupSize";
import NumberOfTables from "./NumberOfTables";
import CreateMatches from "./CreateMatches";
import StartEvent from "./StartEvent";
import MatchWarning from "./MatchWarning";
import SendPromptDialog from "./SendPromptDialog";
import UndoDialog from "./UndoDialog";
import Scanner from "../../Scanner/Scanner";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
import { MatchContext } from "../../../contexts/MatchContext";
import { toast } from "../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MatchingControlPanel = ({ percentages }) => {
  const { pairingEvent, setAttendees, setPercentages, pairingData } =
    useContext(EventContext);
  const { getData, filters } = useContext(MatchContext);
  const [syncing, setSyncing] = useState(false);
  const [disableSync, setDisableSync] = useState(true);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [refreshScoresDialogOpen, setRefreshScoresDialogOpen] = useState(false);
  const [dontShowRefreshWarning, setDontShowRefreshWarning] = useState(false);

  useEffect(() => {
    if (pairingEvent) {
      if (pairingEvent?.event?.status === "scheduled") {
        setDisableSync(false);
      } else {
        setDisableSync(true);
      }
    }
  }, [pairingEvent]);

  useEffect(() => {
    if (pairingEvent?.event?.status === "active" && pairingData?.canUndo) {
      setCanUndo(true);
    } else {
      setCanUndo(false);
    }
  }, [pairingEvent, pairingData]);

  const handleSync = async () => {
    setSyncing(true);
    let result = await request(
      `events/eventbrite/syncattendees?eventId=${pairingEvent?.event?._id}`,
    );
    if (result.success) {
      toast.success("Attendees synced!");
    }
    await getEventAttendees(pairingEvent?.event?._id);
    setSyncing(false);
  };

  const getEventAttendees = async (eventId) => {
    const result = await request(`pairingevent/attendees?eventId=${eventId}`);
    if (!result.success) {
      return;
    }
    setAttendees(result.attendees);
    // setPercentages(result.percentages);
    const pResult = await request(
      `pairingevent/percentages?eventId=${eventId}`,
    );
    if (pResult.success) {
      setPercentages(pResult.data);
    }
  };

  const renderPercentages = () => {
    if (percentages?.eventTotals?.boys || percentages?.eventTotals?.girls) {
      return (
        <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
          <Typography variant="caption">
            Boys: {percentages?.eventTotals?.boys}
          </Typography>
          <Typography variant="caption">{" / "}</Typography>
          <Typography variant="caption">
            Girls: {percentages?.eventTotals?.girls}
          </Typography>
          <Typography variant="caption">{" / "}</Typography>
          <Typography variant="caption">
            Unknown: {percentages?.eventTotals?.unknown}
          </Typography>
        </div>
      );
    }
    return <></>;
  };

  const handleConfirmUndo = async () => {
    const body = { pairingEventId: pairingEvent._id };
    const result = await request("pairingevent/undomatches", "post", body);
    if (result.success) {
      getData();
      toast.success("Undo successful");
    }
    setUndoOpen(false);
  };

  const getRefreshScoresStorageKey = () =>
    `refreshMatchScores_dontShow_${pairingEvent?.event?._id}`;

  const handleRefreshScoresClick = () => {
    const dontShow = localStorage.getItem(getRefreshScoresStorageKey());
    if (dontShow === "true") {
      refreshMatchScores();
    } else {
      setDontShowRefreshWarning(false);
      setRefreshScoresDialogOpen(true);
    }
  };

  const handleRefreshScoresConfirm = () => {
    if (dontShowRefreshWarning) {
      localStorage.setItem(getRefreshScoresStorageKey(), "true");
    }
    setRefreshScoresDialogOpen(false);
    refreshMatchScores();
  };

  const refreshMatchScores = async () => {
     const body = { eventId: pairingEvent.event._id, filters, update: true };
     const result = await request(
        "pairingevent/calcmatchscores",
        "post",
        body
      );
      if (result.success) {
       toast.success("Match scores refreshed")
      }
  };

  return (
    <Paper height={3} style={{ padding: 20 }}>
      <Grid container direction="column" alignItems="center" spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" align="center">
            {pairingEvent ? pairingEvent.event.name : ""}
          </Typography>
        </Grid>
        <Grid
          item
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="button">
            Attendees: {percentages?.eventTotals?.total}/
            {pairingEvent ? pairingEvent.event.capacity : ""}
          </Typography>
          {renderPercentages()}
        </Grid>

        <Grid item xs={12} style={{ height: 50, width: "100%" }}>
          <Grid container direction="row">
            <Grid item xs={6}>
              <Grid container direction="column" spacing={3}>
                {["mixer", "dinner"].includes(
                  pairingEvent?.pairingEventType,
                ) ? null : (
                  <Grid item>
                    <NumberOfRounds pairingEvent={pairingEvent} />
                  </Grid>
                )}
                {["group", "dinner"].includes(
                  pairingEvent?.pairingEventType,
                ) ? (
                  <Grid item>
                    <GroupSize pairingEvent={pairingEvent} />
                  </Grid>
                ) : (
                  <></>
                )}
                {["mixer", "dinner"].includes(
                  pairingEvent?.pairingEventType,
                ) ? null : (
                  <Grid item>
                    <NumberOfTables pairingEvent={pairingEvent} />
                  </Grid>
                )}

                {(pairingEvent?.pairingEventType !== "mixer" && pairingEvent?.event?.status === 'active' ) ? (
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleRefreshScoresClick}
                    >
                      Refresh Pairings
                    </Button>
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <Grid container direction="row" spacing={3}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={handleSync}
                        style={{ width: 155, height: 36.5, fontSize: "0.8rem" }}
                        disabled={disableSync}
                      >
                        {syncing ? (
                          <CircularProgress style={{ width: 25, height: 25 }} />
                        ) : (
                          "sync attendees"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <CreateMatches />
                </Grid>
                {canUndo ? (
                  <Grid item>
                    <Button onClick={() => setUndoOpen(true)}>
                      <UndoIcon style={{ marginRight: 10 }} />
                      Undo
                    </Button>
                  </Grid>
                ) : null}

                <Grid item>
                  <StartEvent />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {pairingEvent?.event?.trackIsPresent && (
          <Grid item xs={12} style={{ width: "100%" }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setScannerOpen(true)}
            >
              Scanner
            </Button>
          </Grid>
        )}

        <Grid item xs={6} style={{ height: 50, width: "100%" }}>
          <MatchWarning />
        </Grid>
      </Grid>
      {/* <SaveMatchesDialog open={showSave} close={handleCloseShowSave} save={handleSave}/> */}
      <Dialog
        open={refreshScoresDialogOpen}
        onClose={() => setRefreshScoresDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Refresh Pairings</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Refreshing pairings will only create new pairings for people who
            checked in late and/or update match scores for any checked in attendees
            if they have updated their profile. Refreshing pairings will not
            impact any existing speed dating rounds. Use this if someone showed
            up late and you only want them to see the Guest List or if they updated the profile.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowRefreshWarning}
                onChange={(e) => setDontShowRefreshWarning(e.target.checked)}
              />
            }
            label="Don't show again for this event"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefreshScoresDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRefreshScoresConfirm}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
      <SendPromptDialog
        open={promptDialogOpen}
        close={() => setPromptDialogOpen(false)}
      />
      <UndoDialog
        open={undoOpen}
        close={() => setUndoOpen(false)}
        confirm={handleConfirmUndo}
      />
      <Dialog
        fullScreen
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setScannerOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Scanner
            </Typography>
          </Toolbar>
        </AppBar>
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ flex: 1, overflow: "auto" }}>
            <Scanner />
          </div>
          <div style={{ padding: 16 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setScannerOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </Paper>
  );
};

export default MatchingControlPanel;
